// Usage metadata only: never pass message text, titles, URLs or draft contents.
export type UsageFields = { stage_id?: string; stage_house?: string; visit_id?: string; view_id?: string;
  action?: string; reason?: string; job_id?: string; session_id?: string; panel_id?: string;
  previous_stage_id?: string; visible_ms?: number; elapsed_ms?: number; count?: number; status?: number };
export type UsageEvent = UsageFields & { kind: string; ts: number; event_id: string; page_id: string; seq: number; origin: string; schema: number };
const id = () => globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
let pageId = '', seq = 0, started = false, busy = false;
const queue: UsageEvent[] = [];
const endpoint = '/api/mission/telemetry/cui-usage';
export function usage(kind: string, fields: UsageFields = {}): void {
  if (typeof window === 'undefined') return;
  try {
    queue.push({ ...fields, kind, ts: Date.now(), event_id: id(), page_id: pageId ||= id(), seq: ++seq, origin: location.host, schema: 1 });
    if (queue.length > 500) queue.splice(0, queue.length - 500);
    if (!started) {
      started = true;
      setInterval(() => { void flushUsage(); }, 3000);
      // Defer until visibility/view listeners have appended their final events.
      window.addEventListener('pagehide', () => queueMicrotask(() => { void flushUsage(true); }));
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) queueMicrotask(() => { void flushUsage(true); });
      });
    }
  } catch { /* Tracking must never block navigation or sending. */ }
}
export async function flushUsage(beacon = false): Promise<void> {
  if (busy || !queue.length) return;
  const batch = queue.splice(0, 30);
  const body = JSON.stringify({ events: batch });
  busy = true;
  let delivered = false;
  try {
    if (beacon && navigator.sendBeacon?.(endpoint, new Blob([body], { type: 'application/json' }))) { delivered = true; return; }
    const res = await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true, priority: 'low' });
    if (!res.ok) throw Error('telemetry unavailable');
    delivered = true;
  } catch { queue.unshift(...batch); if (queue.length > 500) queue.splice(0, queue.length - 500); }
  finally { busy = false; if (beacon && delivered && queue.length) void flushUsage(true); }
  // Periodic retries handle failed requests; unload is best effort.
}

// Each visit can have several visible segments. Hidden tabs do not count as reading time.
export class StageVisit {
  private view: { id: string; since: number } | null = null;
  private visit: string;
  private closed = false;
  constructor(private stage: string, private house: string | undefined,
    private emit: (kind: string, fields: UsageFields) => void = usage,
    private now: () => number = () => performance.now(), private makeId: () => string = id) {
    this.visit = makeId();
    this.send('stage_enter');
  }
  private send(kind: string, extra: UsageFields = {}) {
    this.emit(kind, { stage_id: this.stage, stage_house: this.house, visit_id: this.visit, ...extra });
  }
  show() {
    if (this.closed || this.view) return;
    this.view = { id: this.makeId(), since: this.now() };
    this.send('stage_view', { view_id: this.view.id });
  }
  heartbeat() {
    if (this.view) this.send('stage_visible', { view_id: this.view.id, visible_ms: Math.max(0, Math.round(this.now() - this.view.since)) });
  }
  hide(reason: string) {
    if (!this.view) return;
    this.send('stage_view_end', { view_id: this.view.id, reason, visible_ms: Math.max(0, Math.round(this.now() - this.view.since)) });
    this.view = null;
  }
  leave(reason: string) { if (this.closed) return; this.hide(reason); this.send('stage_leave', { reason }); this.closed = true; }
}
